/**
 * REST client for the auth/character endpoints (gateway httpApi.ts).
 * The endpoint is same-origin by default (Vite proxies /api in development, Caddy
 * in production) and configurable via the transport edge (net/platform.ts, R2).
 */

import { getToken, serverHttpBase } from './platform.js';
import type { BodyType, CharacterAppearance } from '@great-cloud/shared/client';

export interface CharacterSummary {
  id: number;
  name: string;
  /** The character's chosen look (D-6; AD-137). */
  appearance: CharacterAppearance;
}

/** One selectable wardrobe option (GET /api/appearance-options). */
export interface AppearanceOption {
  id: string;
  name: string;
  slot: 'face' | 'hair' | 'skin';
  assetRef: string | null;
}

export interface AppearanceOptionsResponse {
  bodyTypes: BodyType[];
  options: AppearanceOption[];
}

export interface AuthResponse {
  token: string;
  expiresAt: string;
  account: { id: number; email: string };
  characters: CharacterSummary[];
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${serverHttpBase()}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  const body: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error =
      typeof body === 'object' && body !== null && 'error' in body
        ? (body as { error: { code?: string; message?: string } }).error
        : {};
    throw new ApiError(
      error.code ?? 'unknown',
      error.message ?? `request failed (${res.status})`,
      res.status,
    );
  }
  return body as T;
}

/**
 * Register's OTHER answer (AD-332 item 1; design §7a): email verification is in
 * force, so the account was created but NO session was issued — the emailed
 * `#/verify=<token>` link is what unlocks login. The client shows check-your-mail
 * copy and offers a resend.
 */
export interface VerifyRequiredResponse {
  verifyRequired: true;
  account: { id: number; email: string };
}

export type RegisterResponse = AuthResponse | VerifyRequiredResponse;

export function isVerifyRequired(res: RegisterResponse): res is VerifyRequiredResponse {
  return (res as VerifyRequiredResponse).verifyRequired === true;
}

export function register(
  email: string,
  password: string,
  characterName: string,
  /** Closed-playtest invite code (AD-078); omit when the server gate is off. */
  accessCode?: string,
): Promise<RegisterResponse> {
  return request<RegisterResponse>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, characterName, accessCode }),
  });
}

/** Resend the signup-verification mail. Always `{ ok: true }` for a well-formed address; 503 verifyUnavailable when mail is off. */
export function requestEmailVerification(email: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/auth/verify/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/** The token is the `#/verify=<token>` fragment the mail carried; does NOT log the player in. */
export function completeEmailVerification(token: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/auth/verify/complete', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/**
 * Login's OTHER answer (AD-314 §4.1; `q-mfa-totp-enrolment`): the account is
 * MFA-enrolled, so the server issued a five-minute `mfa` challenge token
 * instead of a session. Nothing but `mfaVerify` accepts it, and it is held in
 * the AuthScreen's state only — never `setToken()`.
 */
export interface MfaChallengeResponse {
  mfaRequired: true;
  mfaToken: string;
  expiresAt: string;
  /** Which second factor to ask for (AD-332 item 2): 'email' = a code was mailed. Absent on older servers ⇒ 'totp'. */
  method?: 'totp' | 'email';
}

export type LoginResponse = AuthResponse | MfaChallengeResponse;

export function isMfaChallenge(res: LoginResponse): res is MfaChallengeResponse {
  return (res as MfaChallengeResponse).mfaRequired === true;
}

export function login(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

// ---------------------------------------------------------------------------
// The MFA mechanism (AD-314 §4; backend httpApi.ts `/api/auth/mfa/verify` +
// `/api/account/mfa*`). Error codes the screens branch on: mfaFailed ·
// mfaChallengeInvalid · mfaUnavailable · mfaAlreadyEnrolled · mfaNoPending ·
// mfaNotEnrolled · mfaRequiredForModerators · authFailed · rateLimited.
// ---------------------------------------------------------------------------

/** Login, second step: the challenge token + a 6-digit code OR a recovery code → the normal login payload. */
export function mfaVerify(
  mfaToken: string,
  factor: { code: string } | { recoveryCode: string },
): Promise<AuthResponse> {
  return request<AuthResponse>('/api/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ mfaToken, ...factor }),
  });
}

export interface MfaStatus {
  available: boolean;
  enrolled: boolean;
  enrolledAt: string | null;
  recoveryCodesRemaining: number;
  moderator: boolean;
  /** The enrolled ladder tier (AD-332 item 2; design §7b): TOTP outranks email. */
  tier: 'totp' | 'email' | 'none';
  emailAvailable: boolean;
  emailEnrolled: boolean;
  emailEnrolledAt: string | null;
}

export function mfaStatus(): Promise<MfaStatus> {
  return request<MfaStatus>('/api/account/mfa', { method: 'GET', headers: authHeaders() });
}

export interface MfaSetupResponse {
  /** Base32, for manual entry into an authenticator app. Shown ONCE. */
  secret: string;
  /** `otpauth://totp/…` — an authenticator app opens it directly; a QR of it is the same string. */
  otpauthUri: string;
  expiresInMs: number;
}

/** Enrolment step 1: re-enter the password; the secret is held PENDING until `mfaConfirm`. */
export function mfaSetup(password: string): Promise<MfaSetupResponse> {
  return request<MfaSetupResponse>('/api/account/mfa/setup', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ password }),
  });
}

/** Enrolment step 2: the first code from the app. Returns the TEN recovery codes — shown once, never again. */
export function mfaConfirm(code: string): Promise<{ ok: true; recoveryCodes: string[] }> {
  return request<{ ok: true; recoveryCodes: string[] }>('/api/account/mfa/confirm', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ code }),
  });
}

/** Turn MFA off: the password AND a current code or a recovery code (moderators are refused). */
export function mfaDisable(
  password: string,
  factor: { code: string } | { recoveryCode: string },
): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/account/mfa/disable', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ password, ...factor }),
  });
}

// The EMAIL-OTP tier (AD-332 item 2; design §7b) — the minimum tier the
// trading gate accepts. Enrolment round-trips a code to the account's mailbox.

/** Email-tier enrolment step 1: re-enter the password; an 8-digit code is mailed. */
export function mfaEmailRequest(password: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/account/mfa/email/request', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ password }),
  });
}

/** Email-tier enrolment step 2: the mailed code. Other sessions are revoked. */
export function mfaEmailConfirm(code: string): Promise<{ ok: true; enrolledAt: string }> {
  return request<{ ok: true; enrolledAt: string }>('/api/account/mfa/email/confirm', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ code }),
  });
}

/** Turn the email tier off: password alone mails a fresh code (`codeRequired`), password + code completes. */
export function mfaEmailDisable(
  password: string,
  code?: string,
): Promise<{ ok: true; codeRequired?: boolean }> {
  return request<{ ok: true; codeRequired?: boolean }>('/api/account/mfa/email/disable', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(code ? { password, code } : { password }),
  });
}

// ---------------------------------------------------------------------------
// Password reset via email + the session-lifecycle siblings (AD-314 §3.8;
// design docs/security/account-recovery-and-mfa-design-2026-08-19.md).
// Error codes the screens branch on: resetUnavailable (T-10) · badEmail ·
// badPassword · resetInvalid · rateLimited · authFailed.
// ---------------------------------------------------------------------------

/** Always resolves `{ ok: true }` for a well-formed address (T-2); 503 resetUnavailable when mail is off. */
export function requestPasswordReset(email: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/auth/reset/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/** The token is the `#/reset=<token>` fragment the mail carried; does NOT log the player in. */
export function completePasswordReset(token: string, newPassword: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/auth/reset/complete', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
}

/** Re-authenticates with the current password; every OTHER session is revoked. */
export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/account/password', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

/** Deletes the calling session row server-side (204). The caller still clears the local token. */
export function logout(): Promise<unknown> {
  return request<unknown>('/api/auth/logout', { method: 'POST', headers: authHeaders() });
}

export function listCharacters(token: string): Promise<{ characters: CharacterSummary[] }> {
  return request<{ characters: CharacterSummary[] }>('/api/characters', {
    method: 'GET',
    headers: { authorization: `Bearer ${token}` },
  });
}

export function createCharacter(
  token: string,
  name: string,
  appearance?: CharacterAppearance,
): Promise<{ character: CharacterSummary }> {
  return request<{ character: CharacterSummary }>('/api/characters', {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: JSON.stringify({ name, appearance }),
  });
}

/** The appearance/customisation catalogue for the creation picker (D-6; AD-137). Public presentation data. */
export function fetchAppearanceOptions(): Promise<AppearanceOptionsResponse> {
  return request<AppearanceOptionsResponse>('/api/appearance-options', { method: 'GET' });
}

// ---------------------------------------------------------------------------
// The banned-player appeal channel (SEC-6 §6.2 item 2; backend httpApi.ts
// `/api/appeal/*`; design record docs/security/banned-player-appeal-channel-design-2026-08-16.md).
// A credential-only login that SKIPS the moderation login gate and issues an
// `appeal`-scoped token; that token reaches ONLY these routes (game REST, both
// sockets and the companion REST refuse it), and it is a fixed-lifetime
// credential (30 min, never slid) — so it is deliberately NOT stored via
// setToken(): it lives in the AppealScreen's state and dies with it.
// ---------------------------------------------------------------------------

export interface AppealSession {
  token: string;
  expiresAt: string;
}

/** One row of the account's OWN enforcement record (GET /api/appeal/actions). */
export interface AppealActionSummary {
  id: number;
  action: 'warn' | 'mute' | 'kick' | 'suspend' | 'ban';
  reason: string | null;
  createdAt: string;
  expiresAt: string | null;
  liftedAt: string | null;
  /** False only for a kick (a point event with nothing in force to lift). */
  appealable: boolean;
  appeal: {
    status: 'open' | 'upheld' | 'overturned' | 'modified';
    createdAt: string;
    decidedAt: string | null;
    decisionReason: string | null;
  } | null;
}

export function appealLogin(email: string, password: string): Promise<AppealSession> {
  return request<AppealSession>('/api/appeal/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function listAppealActions(
  appealToken: string,
): Promise<{ actions: AppealActionSummary[] }> {
  return request<{ actions: AppealActionSummary[] }>('/api/appeal/actions', {
    method: 'GET',
    headers: { authorization: `Bearer ${appealToken}` },
  });
}

export function fileAppeal(
  appealToken: string,
  actionId: number,
  statement: string,
): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/appeal', {
    method: 'POST',
    headers: { authorization: `Bearer ${appealToken}` },
    body: JSON.stringify({ actionId, statement }),
  });
}

// ---------------------------------------------------------------------------
// Storefront REST (PXL). The monetisation surface (Gleam, cosmetics, Cloud
// Caches) is a SEPARATE request/response client from the gameplay socket — the
// AD-024 wall is structural. Auth uses the stored session token.
// ---------------------------------------------------------------------------

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

export function getStoreCatalogue(): Promise<unknown> {
  return request<unknown>('/api/store/catalogue', { method: 'GET', headers: authHeaders() });
}

export function getStoreWallet(): Promise<unknown> {
  return request<unknown>('/api/store/wallet', { method: 'GET', headers: authHeaders() });
}

export interface StoreBundlesResponse {
  mode: 'stub' | 'checkout';
  bundles: Array<{
    id: string;
    gleam: number;
    priceCents: number;
    currency: string;
    displayPrice: string;
  }>;
  /**
   * The CANONICAL UK/EU express-consent wording (payments-stripe.md §2a) the
   * checkout UI must render; the client only assents, it never supplies text.
   */
  checkoutConsent?: {
    version: string;
    wording: { immediateSupply: string; withdrawalLoss: string };
  };
}

/** The server-defined Gleam bundle ladder + live payment mode (O-009/AD-201). */
export function getStoreBundles(): Promise<StoreBundlesResponse> {
  return request<StoreBundlesResponse>('/api/store/bundles', {
    method: 'GET',
    headers: authHeaders(),
  });
}

/** STUB-mode instant credit (dev/test). The server catalog defines the amount. */
export function storeTopup(bundleId: string): Promise<unknown> {
  return request<unknown>('/api/store/topup', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ bundleId }),
  });
}

/**
 * Checkout-mode purchase: returns the hosted Stripe Checkout URL to open.
 * `consented` = the player ticked the express-consent box (immediate supply +
 * loss of the 14-day right — §2a); the server refuses `consentRequired` without it.
 */
export function storeCheckout(
  bundleId: string,
  consented: boolean,
): Promise<{ url: string; sessionId: string }> {
  return request<{ url: string; sessionId: string }>('/api/store/checkout', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      bundleId,
      consent: { immediateSupply: consented, acknowledgeWithdrawalLoss: consented },
    }),
  });
}

export interface StorePacksResponse {
  mode: 'stub' | 'checkout';
  /** On-sale founder packs only, lowest tier first (founder-packs.md §7; AD-661 F2). */
  packs: Array<{
    id: string;
    tier: number;
    rung: string;
    cosmetics: Array<{ id: string; name: string; kind: string; rarity?: string }>;
    gleam: number;
    /** Display-only real-money equivalent of `gleam` (§5 Display; AD-024), server-formatted. */
    gleamRealMoneyEquivalent: string;
    priceCents: number;
    currency: string;
    closesAt: string | null;
    /** §3a item 3 — present for a holder of a lower tier; a prior purchase, never a discount. */
    upgrade?: {
      fromPackId: string;
      creditCents: number;
      payCents: number;
      /** `G_j − G_i` — the Gleam the upgrade credits (F2 gate r2 K18), with its equivalent. */
      gleam: number;
      gleamRealMoneyEquivalent: string;
    };
  }>;
  /** Derived from the purchase ledger (§7), never from holdings. */
  founder: {
    founder: boolean;
    tier: number | null;
    packId: string | null;
    /** A live link whose chain is broken (§3a item 6; K6/K20): every pack checkout refuses. */
    brokenChain?: true;
  };
  checkoutConsent?: StoreBundlesResponse['checkoutConsent'];
}

/** The founder-pack shelf + the caller's founder status (AD-661 F2 slice 5 server half). */
export function getStorePacks(): Promise<StorePacksResponse> {
  return request<StorePacksResponse>('/api/store/packs', {
    method: 'GET',
    headers: authHeaders(),
  });
}

/**
 * Pack checkout (founder-packs.md §3, §3a): `upgrade` asks for the pair price from the tier the
 * server says the account holds. Same §2a consent assertions as a bundle checkout.
 */
export function storePackCheckout(
  packId: string,
  upgrade: boolean,
  consented: boolean,
): Promise<{ url: string; sessionId: string; mfaPrompt?: boolean }> {
  return request<{ url: string; sessionId: string; mfaPrompt?: boolean }>('/api/store/checkout', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      packId,
      ...(upgrade ? { upgrade: true } : {}),
      consent: { immediateSupply: consented, acknowledgeWithdrawalLoss: consented },
    }),
  });
}

/** E3-held founder grants waiting on 2FA (founder-packs.md §7). */
export function getHeldGrants(): Promise<{ held: Array<{ cosmeticIds: string[]; at: string }> }> {
  return request<{ held: Array<{ cosmeticIds: string[]; at: string }> }>('/api/store/grants/held', {
    method: 'GET',
    headers: authHeaders(),
  });
}

/** Claim every held grant; 403 `mfaRequired` until 2FA is enabled. */
export function claimHeldGrants(): Promise<{ claimed: string[] }> {
  return request<{ claimed: string[] }>('/api/store/grants/claim', {
    method: 'POST',
    headers: authHeaders(),
  });
}

/** The caller's live prestige offers — the login prompt's state (AD-779 S2/S3). */
export function getPrestigeOffers(): Promise<{
  offers: Array<{ cosmeticId: string; expiresAt: string }>;
}> {
  return request<{ offers: Array<{ cosmeticId: string; expiresAt: string }> }>(
    '/api/store/prestige/offers',
    { method: 'GET', headers: authHeaders() },
  );
}

/** Accept the caller's own prestige offer; 404 `noOffer` · 410 `offerLapsed` · 403 `banned`. */
export function acceptPrestigeOffer(
  cosmeticId: string,
): Promise<{ cosmeticId: string; outcome: string; held: boolean }> {
  return request<{ cosmeticId: string; outcome: string; held: boolean }>(
    '/api/store/prestige/accept',
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ cosmeticId }) },
  );
}

/**
 * Accept by the offer mail's `#/prestige-accept=<token>` link (AD-779 S5): public, single-use,
 * bound to that offer; does NOT log the player in. 400 `tokenInvalid` · 410 · 403.
 */
export function acceptPrestigeOfferByToken(
  token: string,
): Promise<{ cosmeticId: string; outcome: string; held: boolean }> {
  return request<{ cosmeticId: string; outcome: string; held: boolean }>(
    '/api/store/prestige/accept-token',
    { method: 'POST', body: JSON.stringify({ token }) },
  );
}

/** Opt out of offer mail by the mail's `#/prestige-unsubscribe=<token>` link (AD-779 S5). */
export function unsubscribeOfferMail(token: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/store/prestige/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export function storeBuy(cosmeticId: string): Promise<unknown> {
  return request<unknown>('/api/store/buy', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ cosmeticId }),
  });
}

export function getCache(id: string): Promise<unknown> {
  return request<unknown>(`/api/store/cache/${id}`, { method: 'GET', headers: authHeaders() });
}

export function openCache(id: string): Promise<unknown> {
  return request<unknown>(`/api/store/cache/${id}/open`, {
    method: 'POST',
    headers: authHeaders(),
  });
}
